import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ClipboardCheck, Plus, Copy, Users } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

export default function ConsentForms() {
  const [forms, setForms] = useState([]);
  const [studies, setStudies] = useState([]);
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState("");
  const [form, setForm] = useState({
    study_id: "", version: "1.0", title: "",
    body_markdown: "",
    required_acknowledgements_text: "",
  });

  const loadForms = useCallback(() => {
    api.get("/studies").then(({ data }) => setStudies(data)).catch((e) => logger.warn("Studies:", e?.response?.status));
    api.get("/clinical/consent/records", { params: selectedStudy ? { study_id: selectedStudy } : {} })
      .then(({ data }) => setRecords(data))
      .catch((e) => logger.warn("Records:", e?.response?.status));
  }, [selectedStudy]);
  useEffect(() => { loadForms(); }, [loadForms]);

  // We keep the created forms in localStorage-lite via a lightweight in-memory list; primary source is DB via records.
  // For visibility, we also just list created forms this session:
  const [sessionForms, setSessionForms] = useState([]);

  const submit = async () => {
    try {
      const acks = form.required_acknowledgements_text
        .split("\n").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/clinical/consent/forms", {
        study_id: form.study_id,
        version: form.version,
        title: form.title,
        body_markdown: form.body_markdown,
        required_acknowledgements: acks,
      });
      toast.success("Consent form created.");
      setSessionForms((prev) => [data, ...prev]);
      setOpen(false);
      setForm({ study_id: "", version: "1.0", title: "", body_markdown: "", required_acknowledgements_text: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/consent/sign/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Subject link copied to clipboard.");
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="vq-overline mb-2 flex items-center gap-2"><ClipboardCheck className="w-3 h-3" /> Clinical · eConsent</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">eConsent Forms</h1>
            <p className="text-slate-600 mt-2 max-w-3xl">
              ICH-GCP §4.8 · ISO 14155 §4.7 · 21 CFR 50 aligned e-signature capture with immutable audit of subject
              acknowledgements, timestamp, and IP.
            </p>
          </div>
          <Button data-testid="new-consent-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> New consent form</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="vq-card overflow-hidden">
            <div className="p-3 border-b bg-slate-50 font-display font-bold text-sm">Recently created (this session)</div>
            <table className="w-full text-sm">
              <tbody data-testid="consent-forms-tbody">
                {sessionForms.length === 0 && (
                  <tr><td className="text-center px-3 py-6 text-slate-400">Create a consent form to get a shareable subject link.</td></tr>
                )}
                {sessionForms.map((f) => (
                  <tr key={f.form_id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{f.title}</div>
                      <div className="text-xs text-slate-500">{f.study_id} · v{f.version}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" data-testid={`copy-link-${f.form_id}`} onClick={() => copyLink(f.token)}>
                        <Copy className="w-3 h-3 mr-1" /> Copy link
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="vq-card overflow-hidden">
            <div className="p-3 border-b bg-slate-50 flex items-center justify-between">
              <div className="font-display font-bold text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Signed consents</div>
              <div>
                <Select value={selectedStudy || "__all"} onValueChange={(v) => setSelectedStudy(v === "__all" ? "" : v)}>
                  <SelectTrigger data-testid="consent-filter-study" className="w-48"><SelectValue placeholder="All studies" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All studies</SelectItem>
                    {studies.map((s) => <SelectItem key={s.study_id} value={s.study_id}>{s.title || s.study_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <table className="w-full text-sm">
              <tbody data-testid="consent-records-tbody">
                {records.length === 0 && <tr><td colSpan="3" className="text-center px-3 py-6 text-slate-400">No signed consents yet.</td></tr>}
                {records.map((r) => (
                  <tr key={r.consent_id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.subject_name}</div>
                      <div className="text-xs text-slate-500">{r.form_title} · v{r.form_version}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <Badge variant="secondary">{r.acknowledged?.length || 0} ack</Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{new Date(r.signed_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>New consent form</DialogTitle>
              <DialogDescription>Publishes a tokenised subject-facing link. Every field the subject must acknowledge counts as a required check-box.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Study</Label>
                <Select value={form.study_id} onValueChange={(v) => setForm({ ...form, study_id: v })}>
                  <SelectTrigger data-testid="cf-study"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{studies.map((s) => <SelectItem key={s.study_id} value={s.study_id}>{s.title || s.study_id}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Version</Label>
                <Input data-testid="cf-version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Title</Label>
                <Input data-testid="cf-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Adult participant informed consent" />
              </div>
              <div className="col-span-2">
                <Label>Body (markdown, min 50 chars)</Label>
                <Textarea data-testid="cf-body" rows={8} value={form.body_markdown} onChange={(e) => setForm({ ...form, body_markdown: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Required acknowledgements (one per line)</Label>
                <Textarea data-testid="cf-acks" rows={4} value={form.required_acknowledgements_text} onChange={(e) => setForm({ ...form, required_acknowledgements_text: e.target.value })} placeholder="I understand the purpose of this study.&#10;I understand my participation is voluntary.&#10;I consent to data collection." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="cf-submit" onClick={submit}
                      disabled={!form.study_id || !form.title || form.body_markdown.length < 50}>Create form</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
